import type { MaterialsResponse, MaterialOption } from '@roomtreatment/core';

type ApiAlphaSample = { freq_hz: number; alpha: number };

type RawMaterial = {
  material_id?: string;
  key?: string;
  label: string;
  alpha?: number[] | ApiAlphaSample[];
  kind?: string;
};

type RawCatalog = {
  default?: string;
  options?: MaterialOption[];
  materials?: RawMaterial[];
};

function alphaToNumbers(alpha: RawMaterial['alpha']): number[] {
  if (!alpha?.length) return [];
  if (typeof alpha[0] === 'number') return alpha as number[];
  return (alpha as ApiAlphaSample[]).map((s) => s.alpha);
}

function normalizeOption(m: RawMaterial): MaterialOption | null {
  const key = m.material_id ?? m.key;
  if (!key) return null;
  return { key, label: m.label, alpha: alphaToNumbers(m.alpha) };
}

function normalizeCatalog(raw: RawCatalog | undefined): { default: string; options: MaterialOption[] } {
  if (!raw) return { default: '', options: [] };
  if (raw.options?.length) {
    return { default: raw.default ?? '', options: raw.options };
  }
  const options = (raw.materials ?? [])
    .map(normalizeOption)
    .filter((o): o is MaterialOption => o !== null);
  return { default: raw.default ?? '', options };
}

/** Map `/api/admin/materials` catalog_summary_for_api() to MaterialsResponse. */
export function normalizeMaterialsResponse(body: Record<string, unknown>): MaterialsResponse {
  return {
    octave_bands: (body.octave_bands as number[]) ?? [],
    floor: normalizeCatalog(body.floor as RawCatalog | undefined),
    wall: normalizeCatalog(body.wall as RawCatalog | undefined),
  };
}

/** Wall acoustic picker — glazed opening curve is window-only. */
export function wallAcousticMaterialOptions(options: MaterialOption[]): MaterialOption[] {
  return options.filter((o) => o.key !== 'glass_heavy');
}
